/**
 * Automations Metrics and Calculation Logic
 *
 * Defines completion calculation, status metrics, category determination,
 * and data availability checks for Automations.
 */

// Get automation completion metrics
export const getAutomationMetrics = (automation) => {
  if (!automation) return { completion: 0, completedFields: 0, totalFields: 0 };

  const requiredFields = [
    "name",
    "type",
    "status",
    "automationSteps",
    "triggers",
  ];

  const optionalFields = [
    "description",
    "totalRuns",
    "successfulRuns",
    "failedRuns",
    "lastRun",
    "attachedToDeals",
    "tags",
    "metrics",
  ];

  const allFields = [...requiredFields, ...optionalFields];

  const completedFields = allFields.filter((field) => {
    const value = automation[field];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== null && value !== undefined && value !== "";
  }).length;

  const completion = Math.round((completedFields / allFields.length) * 100);

  const completedRequiredFields = requiredFields.filter((field) => {
    const value = automation[field];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== null && value !== undefined && value !== "";
  }).length;

  return {
    completion,
    completedFields,
    totalFields: allFields.length,
    requiredFields: requiredFields.length,
    completedRequiredFields,
  };
};

// Calculate overall metrics for a list of automations
export const calculateAutomationsMetrics = (automations = []) => {
  const total = automations.length;

  const statusCounts = automations.reduce((acc, automation) => {
    const status = automation.status || "inactive";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const typeCounts = automations.reduce((acc, automation) => {
    const type = automation.type || "workflow";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Calculate average completion
  const totalCompletion = automations.reduce((sum, automation) => {
    return sum + getAutomationMetrics(automation).completion;
  }, 0);

  const averageCompletion = total > 0 ? Math.round(totalCompletion / total) : 0;

  // Calculate total runs and success rate
  const totalRuns = automations.reduce((sum, automation) => {
    return sum + (automation.totalRuns || 0);
  }, 0);

  const totalSuccessfulRuns = automations.reduce((sum, automation) => {
    return sum + (automation.successfulRuns || 0);
  }, 0);

  const overallSuccessRate =
    totalRuns > 0 ? Math.round((totalSuccessfulRuns / totalRuns) * 100) : 0;

  // Calculate in-progress count
  const totalInProgress = automations.reduce((sum, automation) => {
    return sum + (automation.inProgress || 0);
  }, 0);

  // Calculate automations with recent activity (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const recentlyActive = automations.filter((automation) => {
    if (!automation.lastRun) return false;
    const lastRunDate = new Date(automation.lastRun);
    return lastRunDate >= thirtyDaysAgo;
  }).length;

  return {
    total,
    active: statusCounts.active || 0,
    paused: statusCounts.paused || 0,
    inactive: statusCounts.inactive || 0,
    archived: statusCounts.archived || 0,

    // Type breakdown
    email: typeCounts.email || 0,
    document: typeCounts.document || 0,
    data_processing: typeCounts.data_processing || 0,
    workflow: typeCounts.workflow || 0,
    notification: typeCounts.notification || 0,
    integration: typeCounts.integration || 0,

    // Performance metrics
    totalRuns,
    totalSuccessfulRuns,
    overallSuccessRate,
    totalInProgress,
    averageCompletion,
    recentlyActive,

    // Status percentages
    activePercentage:
      total > 0 ? Math.round(((statusCounts.active || 0) / total) * 100) : 0,
    pausedPercentage:
      total > 0 ? Math.round(((statusCounts.paused || 0) / total) * 100) : 0,
    recentlyActivePercentage:
      total > 0 ? Math.round((recentlyActive / total) * 100) : 0,
  };
};

// Determine automation category for icons
export const getAutomationCategory = (automation) => {
  if (!automation.type) return "workflow";

  const type = automation.type.toLowerCase();

  const categoryMap = {
    email: "email",
    document: "document",
    data_processing: "data",
    workflow: "workflow",
    notification: "notification",
    integration: "integration",
  };

  return categoryMap[type] || "workflow";
};

// Check data availability for an automation
export const checkAutomationDataAvailability = (automation) => {
  const metrics = getAutomationMetrics(automation);

  return {
    hasBasicInfo: !!(automation.name && automation.type && automation.status),
    hasSteps: !!(
      automation.automationSteps && automation.automationSteps.length > 0
    ),
    hasTriggers: !!(automation.triggers && automation.triggers.length > 0),
    hasRunData: !!automation.totalRuns,
    hasAttachedDeals: !!(
      automation.attachedToDeals && automation.attachedToDeals.length > 0
    ),
    hasMetrics: !!automation.metrics,
    completionLevel: metrics.completion,

    // Quality indicators
    isComplete: metrics.completion === 100,
    isWellFormed: metrics.completion >= 80,
    needsAttention: metrics.completion < 50,

    // Missing critical data
    missingCritical: metrics.completedRequiredFields < metrics.requiredFields,

    // Performance indicators
    hasRecentActivity:
      automation.lastRun &&
      new Date(automation.lastRun) >
        new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    hasFailures: automation.failedRuns > 0,
    isHighPerforming:
      automation.totalRuns > 0 &&
      automation.successfulRuns / automation.totalRuns >= 0.95,
  };
};

// Generate status summary for automations
export const getAutomationsStatusSummary = (automations = []) => {
  const metrics = calculateAutomationsMetrics(automations);

  return [
    {
      label: "Total Automations",
      value: metrics.total,
      type: "count",
      color: "blue",
    },
    {
      label: "Active",
      value: metrics.active,
      percentage: metrics.activePercentage,
      type: "status",
      color: "green",
    },
    {
      label: "Success Rate",
      value: `${metrics.overallSuccessRate}%`,
      type: "percentage",
      color:
        metrics.overallSuccessRate >= 95
          ? "green"
          : metrics.overallSuccessRate >= 80
            ? "yellow"
            : "red",
    },
    {
      label: "In Progress",
      value: metrics.totalInProgress,
      type: "count",
      color: metrics.totalInProgress > 0 ? "blue" : "gray",
    },
  ];
};
